/**
 * Imaginile vitrinei.
 *
 * Trei lucruri, in ordinea in care se vad la client:
 *
 *  1. **Locul e rezervat inainte ca poza sa ajunga.** Raportul de aspect e pe
 *     container, nu pe `<img>`, iar `width`/`height` reale (cand catalogul le
 *     stie — `OfferImageDto`) intra pe element. Fara ele, fiecare poza care
 *     soseste impinge continutul de dedesubt: pe un meniu lung, lista sare sub
 *     degetul care tocmai voia sa apese un produs.
 *  2. **Doar prima poza se incarca devreme.** Restul sunt `lazy` + `async`.
 *     `priority` exista pentru exact un element per pagina — cel mai mare de pe
 *     primul ecran (hero-ul vitrinei), care decide LCP-ul.
 *  3. **Lipsa pozei nu e eroare** (ADR-024): produsul fara poza are aspectul lui,
 *     nu un chenar rupt. La fel si poza care nu se incarca — se cade pe acelasi
 *     aspect, o singura data, fara bucla de reincercari.
 *
 * `srcSet` se genereaza doar cand adresa suporta redimensionare (`?w=`). Astazi
 * platforma re-gazduieste o singura varianta, deci lista ramane goala si nu se
 * inventeaza adrese care ar da 404; cand va exista un proxy de imagini, e
 * singurul loc de schimbat.
 */

import { useState, type CSSProperties } from "react";

/** Latimile pentru care are rost sa ceri variante, cand adresa le suporta. */
const RESIZE_WIDTHS = [160, 240, 320, 480, 640] as const;

/** Adresa accepta un parametru de latime? Doar atunci `srcSet` spune adevarul. */
function supportsResize(url: string): boolean {
  return /[?&]w=\d+/.test(url);
}

function buildSrcSet(url: string): string | undefined {
  if (!supportsResize(url)) return undefined;
  return RESIZE_WIDTHS.map((w) => `${url.replace(/([?&]w=)\d+/, `$1${w}`)} ${w}w`).join(", ");
}

export interface ResponsiveImageProps {
  src: string | null | undefined;
  alt: string;
  /** Raportul de aspect al LOCULUI rezervat, ca lista sa nu sara. */
  ratio?: `${number}/${number}`;
  /** Dimensiunile reale, cand catalogul le cunoaste: elimina orice deplasare. */
  width?: number | null;
  height?: number | null;
  /**
   * Ce lățime va ocupa imaginea la fiecare prag. Conteaza doar impreuna cu
   * `srcSet`, dar se declara oricum: in ziua in care apare proxy-ul, imaginile
   * incep sa fie servite la dimensiunea potrivita fara alta modificare.
   */
  sizes?: string;
  /** Exact un element per pagina: cel care decide LCP-ul. */
  priority?: boolean;
  className?: string;
  /** Ce se arata cand nu exista poza sau nu s-a putut incarca. */
  fallback?: React.ReactNode;
  rounded?: boolean;
  position?: string;
}

export function ResponsiveImage({
  src,
  alt,
  ratio = "3/2",
  width,
  height,
  sizes = "(min-width: 1024px) 220px, (min-width: 640px) 30vw, 40vw",
  priority = false,
  className = "",
  fallback,
  rounded = true,
  position = "center",
}: ResponsiveImageProps) {
  /*
    Starile se retin PE ADRESA, nu pe componenta.
    Aceeasi instanta primeste adrese diferite — galeria din fisa produsului
    schimba `src` la fiecare miniatura. Cu doua booleene simple, o poza moarta
    lasa `failed` pornit si peste urmatoarea adresa, buna: cardul ramanea pe
    imaginea de rezerva pana la reincarcarea paginii. Simetric, `loaded` ramas
    din poza precedenta ar fi lasat-o pe cea noua la `opacity-0`, adica
    incarcata, dar invizibila.
  */
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const failed = typeof src === "string" && src.length > 0 && failedSrc === src;
  const loaded = typeof src === "string" && src.length > 0 && loadedSrc === src;

  const shell: CSSProperties = { aspectRatio: ratio };
  const shellClass = [
    "relative overflow-hidden bg-surface-raised",
    rounded ? "rounded-card" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const usable = typeof src === "string" && src.length > 0 && !failed;

  if (!usable) {
    return (
      <div className={shellClass} style={shell} aria-hidden={fallback === undefined ? true : undefined}>
        {fallback ?? null}
      </div>
    );
  }

  return (
    <div className={shellClass} style={shell}>
      <img
        src={src}
        srcSet={buildSrcSet(src)}
        sizes={sizes}
        alt={alt}
        width={width ?? undefined}
        height={height ?? undefined}
        // `eager` + prioritate mare DOAR pe elementul care decide LCP-ul; pe
        // restul, `lazy` tine cererile in afara drumului critic.
        loading={priority ? "eager" : "lazy"}
        // React 18.3 nu recunoaște încă forma camelCase și o raportează la
        // fiecare imagine. Atributul HTML standard este lowercase; pe imaginile
        // obișnuite îl omitem, fiindcă prioritatea implicită este deja `auto`.
        {...(priority ? { fetchpriority: "high" } : {})}
        decoding={priority ? "sync" : "async"}
        style={{ objectPosition: position }}
        // Cheia e adresa: o cadere opreste exact adresa care a cazut, nu
        // componenta, deci urmatoarea poza din galerie porneste curata.
        onLoad={() => setLoadedSrc(src)}
        onError={() => setFailedSrc(src)}
        className={[
          "absolute inset-0 h-full w-full object-cover",
          // Aparitia scurta ascunde saltul de decodare, fara sa intarzie nimic:
          // imaginea e deja in pagina, doar opacitatea se schimba.
          "transition-opacity duration-200",
          loaded ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
    </div>
  );
}

/**
 * Locul unei imagini care inca nu a sosit — sau al unui produs fara poza.
 *
 * Nu e o imagine goala: pe un card fara poza, un dreptunghi gri identic cu cel
 * de incarcare l-ar face sa para permanent in curs de incarcare.
 */
export function ImageFallback({ label }: { label?: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-surface-raised">
      <span aria-hidden="true" className="text-2xl opacity-40">
        {label ?? "🍽"}
      </span>
    </div>
  );
}
